import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { forgotPassword, signup } from '../api';
import { ErrorMsg, SuccessMsg, Spinner, Logo } from '../components/UI';

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const reset = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, username);
      // Auto-login after signup
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setSuccess('Check your email for a password reset link.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      // Page will redirect to Google — no navigate() needed here
    } catch (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-green-100 rounded-full opacity-40" />
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-amber-100 rounded-full opacity-50" />
      </div>

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto mb-3" />
          <h1 className="font-serif font-bold text-3xl text-green-900">GolfBoard Scores</h1>
          <p className="text-gray-500 text-sm mt-1">Track scores. Climb leaderboards.</p>
        </div>

        <div className="card p-6 fade-in">

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <>
              <h2 className="font-serif font-semibold text-xl text-green-900 mb-5">Welcome back</h2>

              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-4"
              >
                {googleLoading ? <Spinner size="sm" /> : (
                  <>
                    <GoogleIcon />
                    Continue with Google
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Email</label>
                  <input type="email" className="input-field" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Password</label>
                  <input type="password" className="input-field" placeholder="••••••••"
                    value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                <ErrorMsg message={error} />
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Sign In'}
                </button>
              </form>

              <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col gap-2 text-center">
                <button onClick={() => reset('forgot')}
                  className="text-sm text-green-700 hover:text-green-900 transition-colors">
                  Forgot your password?
                </button>
                <button onClick={() => reset('signup')}
                  className="text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors">
                  New here? Create an account →
                </button>
              </div>
            </>
          )}

          {/* ── SIGN UP ── */}
          {mode === 'signup' && (
            <>
              <h2 className="font-serif font-semibold text-xl text-green-900 mb-5">Create account</h2>

              {/* Google Sign Up */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-4"
              >
                {googleLoading ? <Spinner size="sm" /> : (
                  <>
                    <GoogleIcon />
                    Sign up with Google
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Username</label>
                  <input type="text" className="input-field" placeholder="e.g. tiger_woods"
                    value={username} onChange={e => setUsername(e.target.value)}
                    minLength={3} maxLength={20} pattern="^[a-zA-Z0-9_]+$"
                    title="Letters, numbers, and underscores only" required />
                  <p className="text-xs text-gray-400 mt-1">3–20 characters. Letters, numbers, and underscores only.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Email</label>
                  <input type="email" className="input-field" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Password</label>
                  <input type="password" className="input-field" placeholder="At least 8 characters"
                    value={password} onChange={e => setPassword(e.target.value)}
                    minLength={8} required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Confirm Password</label>
                  <input type="password" className="input-field"
                    placeholder="Repeat your password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    required />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords don't match.</p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-xs text-green-600 mt-1">✓ Passwords match.</p>
                  )}
                </div>
                <ErrorMsg message={error} />
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={loading || (confirmPassword && password !== confirmPassword)}>
                  {loading ? <Spinner size="sm" /> : 'Create Account'}
                </button>
              </form>

              <button onClick={() => reset('login')}
                className="w-full text-center text-sm text-green-700 hover:text-green-900 mt-4 transition-colors">
                ← Already have an account? Sign in
              </button>
            </>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {mode === 'forgot' && (
            <>
              <h2 className="font-serif font-semibold text-xl text-green-900 mb-1">Reset Password</h2>
              <p className="text-sm text-gray-500 mb-5">We'll send a reset link to your email.</p>
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Email</label>
                  <input type="email" className="input-field" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <ErrorMsg message={error} />
                <SuccessMsg message={success} />
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Send Reset Link'}
                </button>
              </form>

              <button onClick={() => reset('login')}
                className="w-full text-center text-sm text-green-700 hover:text-green-900 mt-4 transition-colors">
                ← Back to sign in
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// Inline Google SVG icon — no external dependency needed
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}